import type { AccountInfo, RedirectRequest } from '@azure/msal-browser'
import type { ExternalAuthConfig, IfUser } from '@if/mui'
import { reportErrorToServer } from './services/error-logger'
import { CLEAR_CACHE_EXCEPTION_KEY, CLEAR_CACHE_EXCEPTION_PREFIX, EPermissions } from './types/Constants'

const env = import.meta.env

const CLIENT_ID = env.VITE_REGISTRATION_CLIENT_ID
const AUTHORITY_BASE = env.VITE_B2C_AUTHORITY // ends with '/'
const SIGNIN_POLICY = env.VITE_B2C_SIGNIN_POLICY
const FEDERATION_POLICY = env.VITE_B2C_FEDERATION_SIGNIN_POLICY
const RESET_POLICY = env.VITE_B2C_RESET_POLICY
const KNOWN_AUTHORITIES = env.VITE_B2C_KNOWN_AUTHORITIES

const environment = env.VITE_ENV === 'local' ? 'dev' : env.VITE_ENV

/** The API base, trailing-slash-normalized (dev points cross-origin at VITE_API_URL). */
function apiBaseUrl(): string {
  const base = env.PROD ? `${window.location.origin}/api` : env.VITE_API_URL
  return base.replace(/\/?$/, '/')
}

/** The policy the account actually signed in under (its `tfp`/`acr`), else sign-in. */
const policyOf = (account: AccountInfo): string => {
  const claims = account.idTokenClaims as { tfp?: string; acr?: string } | undefined
  return claims?.tfp ?? claims?.acr ?? SIGNIN_POLICY
}

/**
 * True when the active account entered via the Iceberg-AD federation policy
 * (Phase 2D). Their profile is managed in Entra ID, not B2C, so the profile
 * dialog renders read-only for them.
 */
export function isFederatedUser(account: AccountInfo | undefined | null): boolean {
  if (!account) return false
  return !!FEDERATION_POLICY && policyOf(account).toLowerCase() === FEDERATION_POLICY.toLowerCase()
}

/** The interactive "change password" redirect (B2C reset policy). */
export const passwordResetRequest: RedirectRequest = {
  scopes: [],
  authority: `${AUTHORITY_BASE}${RESET_POLICY}`,
  extraQueryParameters: { environment },
}

/** The @if/mui auth config for MerchantDashboard's B2C custom-policy setup. */
export function buildAuthConfig(): ExternalAuthConfig {
  return {
    kind: 'external',
    clientId: CLIENT_ID,
    apiScopes: [`${env.VITE_B2C_SCOPE_REF}/api://${CLIENT_ID}/ReadAccess`],
    exchangeEndpoint: `${apiBaseUrl()}Login/GenerateJwtToken`,
    // The backend validates `aud = clientId` and reads B2C identity/extension
    // claims (emails, name, extension_MerchantsList) that ride the ID token.
    exchangeToken: 'idToken',
    authorityBase: AUTHORITY_BASE,
    knownAuthorities: [KNOWN_AUTHORITIES],
    policies: { signIn: SIGNIN_POLICY, passwordReset: RESET_POLICY, federation: FEDERATION_POLICY },
    extraQueryParameters: { environment },
    onLogError: reportErrorToServer,
    onLogout: () => {
      // Mirror the old logout: wipe local cache except the exempt keys (theme) and the
      // device-scoped preference namespace, which belongs to the browser, not the session.
      const persisted: Record<string, string> = {}
      CLEAR_CACHE_EXCEPTION_KEY.forEach((key) => {
        const value = localStorage.getItem(key)
        if (value != null) persisted[key] = value
      })
      Object.keys(localStorage)
        .filter((key) => CLEAR_CACHE_EXCEPTION_PREFIX.some((prefix) => key.startsWith(prefix)))
        .forEach((key) => {
          const value = localStorage.getItem(key)
          if (value != null) persisted[key] = value
        })
      localStorage.clear()
      Object.entries(persisted).forEach(([key, value]) => localStorage.setItem(key, value))
    },
    // Federation login-request shaping: a `?targetMerchantId=`
    // switches to the federation policy and forces a fresh prompt.
    resolveLoginRequest: (url) => {
      const targetMerchantId = url.get('targetMerchantId')
      if (!targetMerchantId) return undefined
      return { policy: FEDERATION_POLICY, prompt: 'login', extraQueryParameters: { targetMerchantId } }
    },
    // Merchant switch on a cached session: force interactive re-login when the URL
    // targets a merchant the cached account isn't for.
    requireInteractiveLogin: ({ url, account }) => {
      const targetMerchantId = url.get('targetMerchantId')
      if (!targetMerchantId) return false
      const cached = (account?.idTokenClaims as { extension_MerchantsList?: string } | undefined)
        ?.extension_MerchantsList
      const nonMatchingMerchant = !!cached && cached !== targetMerchantId
      return !account || nonMatchingMerchant
    },
    // Silent acquire against the policy the account signed in under (federation
    // accounts would otherwise be re-prompted against the default sign-in policy).
    resolveAuthority: (account) => `${AUTHORITY_BASE}${policyOf(account)}`,
  }
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

const csvToSet = (csv: unknown): Set<string> =>
  new Set(
    str(csv)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean),
  )

/**
 * The one app-specific auth function: shape the decoded Iceberg-JWT payload into
 * the design-system `IfUser`. `roles` (CSV) drives the permission booleans that
 * used to live in `useUserPermissions`; `rights` (CSV) drives the rest. Runs on
 * every (re-)exchange, so it must stay pure and cheap.
 */
export function mapClaims(payload: Record<string, unknown>): IfUser {
  const roles = csvToSet(payload.roles)
  const rights = csvToSet(payload.rights)
  const fullName = str(payload.fullName)
  const [firstName, ...rest] = fullName.split(' ')

  return {
    id: str(payload.id),
    name: fullName,
    // TODO: User Story 66651: Ajouter dans le app registration les champs optionel given name, surname
    firstName: firstName ?? '',
    lastName: rest.join(' '),
    email: str(payload.email),
    merchantId: str(payload.merchantId),
    fullName,
    isBannerUser: payload.isBannerUser === true || payload.isBannerUser === 'True',
    language: str(payload.language),
    roles: str(payload.roles),
    rights: {
      hasDetailAccess: roles.has(EPermissions.HasDetailAccess),
      canCreateApplications: roles.has(EPermissions.CanCreateApplications),
      isAdmin: roles.has(EPermissions.IsAdmin),
      canSendWelcomeMessage: roles.has(EPermissions.CanSendWelcomeMessage),
      canAddUser: rights.has('canAddUser'),
      canEditUser: rights.has('canEditUser'),
      canSetReportProperties: rights.has('canSetReportProperties'),
      canManagePromotions: rights.has('canManagePromotions'),
      canViewStatistics: rights.has('canViewStatistics'),
    },
  }
}
